/**
 * Client-facing setup copy for tenant `/onboarding`.
 * Distinguishes workspace UI steps from Meta/Facebook prerequisites
 * and platform rails that Clinch (not the client) configures.
 */

export const WORKSPACE_SETUP_STEPS = [
  {
    key: 'business_complete',
    title: 'Business profile',
    description: 'Create your store listing, pickup address, and delivery modes.',
    path: '/manage/listing-management',
    where: 'Sidebar → Stores',
    who: 'you',
    // Live: any non-terminated store (GET /onboarding/ has_store), same as Help path count:has_store
    liveStore: true,
    how: [
      'Open Stores and click Add store. Starter allows one store; Growth up to three; Scale more — buy extras under Usage & billing when your plan allows.',
      'Fill collection / pickup address and driver instructions. Couriers (Loop and Bob Go) use this when an order is ready.',
      'Under WhatsApp delivery modes, turn on on-demand, courier, and/or customer collect so checkout can offer them.',
    ],
  },
  {
    key: 'shipping_complete',
    title: 'Shipping',
    description: 'Choose Clinch Fulfilment, or connect Bob Go / Loop in the same row.',
    path: '/manage/system?tab=shipping',
    where: 'Sidebar → System Management → Shipping',
    who: 'you',
    liveCheck: 'shipping',
    how: [
      'Open System Management → Shipping and choose Clinch Fulfilment, or connect your own courier accounts in the same row.',
      'If you use your own logistics, save Bob Go and/or Loop keys the same way as payment gateways (encrypted, last four shown).',
      'Pickup address still lives on each store listing.',
    ],
  },
  {
    key: 'payment_gateway_complete',
    title: 'Payment gateway',
    description: 'Choose Clinch Checkout or connect your own Ozow / PayFast / PayGate / Transaction Junction / Payflex / Yoco for shopper payments.',
    path: '/manage/system?tab=payment_gateway',
    where: 'Sidebar → System Management → Payment gateway',
    who: 'you',
    liveCheck: 'payment_gateway',
    how: [
      'Open System Management → Payment gateway. Enable Clinch Checkout and/or add your own Ozow / PayFast / PayGate / Transaction Junction / Payflex / Yoco — both can run at the same time.',
      'Clinch Checkout: shoppers can pay Clinch PayFast; Clinch withholds the billing-rate Platform fee; remainder credits your wallets as a ledger. Stores request withdrawals (same as marketplace) — we don’t auto-pay your bank.',
      'Own rails settle to your merchants at 0% Clinch Platform fee. Clinch Checkout is only available after Clinch configures platform Shopper checkout PayFast (verified + Active).',
      'Until Clinch Checkout is available/selected or your own rails are configured, checkout cannot take payment.',
    ],
  },
  {
    key: 'waba_complete',
    title: 'WhatsApp number',
    description: 'Connect the WhatsApp number customers will message.',
    path: '/manage/company-wabas',
    where: 'Sidebar → WhatsApp Account → Connect WhatsApp number',
    who: 'you',
    hard: true,
    how: [
      'Have ready: a Facebook login that can manage your Facebook Business portfolio (you can create both during signup), your business name and website or social media page, and a phone number that can receive an SMS or call.',
      'The number must not be active on the WhatsApp or WhatsApp Business app. Remove it from the app first, or use a new number.',
      'Open WhatsApp Account and click Connect WhatsApp number. Meta’s signup opens in a new tab: log in, choose or create your business, create the WhatsApp Business profile, then add your number and enter the code Meta sends.',
      'Someone else manages your Facebook business? Click Copy link and send it to them — the link works for 5 days.',
      'Keep the WhatsApp Account page open. It updates by itself when the number is live, and this step is ticked for you. Open the number and set Phone type to Marketplace or Support yourself — connecting does not assign one.',
      'In Meta Commerce Manager, link a Commerce catalog to that number. Without a catalog, product cards and “View catalog” will not work on WhatsApp even if products look fine in Clinch.',
    ],
  },
  {
    key: 'theme_complete',
    title: 'Platform branding',
    description: 'Logo, name, and colours for staff screens (shoppers never see this site).',
    path: '/manage/appearance',
    where: 'Sidebar → My Platform → Platform branding',
    who: 'you',
    how: [
      'Set the platform name first, then upload a logo (it saves immediately and shows in the sidebar, navbar, and login).',
      'Pick a UI suggestion (for example Harbor Sky), then fine-tune colours if needed.',
      'Save. Staff see this on your subdomain — shoppers never visit this site.',
    ],
  },
  {
    key: 'team_complete',
    title: 'Team',
    description: 'Invite inbox staff within your seat limit.',
    path: '/manage/system',
    where: 'Sidebar → System Management → Users',
    who: 'you',
    how: [
      'Open System Management → Users and invite people with their work email.',
      'Assign a role (Roles & permissions tab) so they can use Team Inbox without seeing billing or WABA tokens.',
      'Seat counts follow your plan. Add seats on Usage & billing if you are on Growth.',
    ],
  },
  {
    key: 'plan_complete',
    title: 'Plan & billing',
    description: 'Confirm the package you already paid for, seats, and add-ons.',
    path: '/manage/subscription',
    where: 'Sidebar → My Platform → Usage & billing',
    who: 'you',
    how: [
      'Open Usage & billing and check Starter, Growth, or Scale is the plan you paid for.',
      'Review team seats, AI usage this month (WhatsApp AI chats + Structure with AI + Templates AI), and store limits. Growth can buy extra seats and stores here.',
      'Clinch bills your platform package and usage only — not shopper checkout.',
    ],
  },
]

export const WORKSPACE_SETUP_ALSO_REQUIRED = [
  {
    title: 'Products & WhatsApp catalog',
    where: 'Sidebar → Products',
    path: '/dashboard/products',
    who: 'you',
    how: [
      'Add products with names, prices, images, and variants. Open a product → Status & Flags and fix any WhatsApp catalog gaps, then Save & re-sync.',
      'Meta must already have a catalog linked to your WABA phone (see WhatsApp step). Incomplete groups or missing images mean colours will not show on WhatsApp.',
    ],
  },
  {
    title: 'Message templates & sender numbers',
    where: 'Sidebar → Templates (or Campaigns for broadcasts)',
    path: '/manage/templates',
    who: 'you',
    how: [
      'Create templates under Templates. Meta reviews them — they are not live until approved.',
      'Numbers connected with Connect WhatsApp number: Sync submits the template through Gupshup and the approval shows here. Carousel templates are not supported for these numbers yet.',
      'Setting a number to Marketplace or Support also submits Clinch’s system templates (and, for Marketplace, publishes the checkout forms) — progress shows on WhatsApp Account.',
      'On Sender numbers (Templates → Senders), choose which connected WABA phone sends campaigns and session messages.',
      'Team Inbox is per sender number: pick the number tab, then the customer thread. Contacts lists Team Inbox customers for campaigns.',
    ],
  },
  {
    title: 'Orders, payments, and payouts',
    where: 'Sidebar → My Platform → Money & payouts',
    path: '/manage/settlement',
    who: 'mixed',
    how: [
      'Shoppers pay on WhatsApp with Ozow and PayFast using the gateways you configured under System Management → Payment gateway.',
      'Watch orders under Orders. Money & payouts is your account with Clinch: usage, the Clinch invoice and shipping held.',
      'Wallet is your account with your stores: see store earnings, then approve and release store payout requests. Choose Clinch Fulfilment or your own courier keys under System Management → Shipping.',
    ],
  },
]

export const WORKSPACE_SETUP_CLINCH_HANDLES = [
  {
    title: 'Platform package billing',
    detail:
      'Clinch bills Starter / Growth / Scale packages (and upgrades) plus monitors WhatsApp conversation mix and AI usage. Shopper GMV settles on your own Ozow/PayFast merchants.',
  },
  {
    title: 'Courier APIs',
    detail:
      'Clinch Fulfilment uses Clinch’s Bob Go and Loop accounts. To use your own couriers, save encrypted keys on System Management → Shipping. Pickup address and delivery modes stay on the listing.',
  },
  {
    title: 'WhatsApp Business Platform connection',
    detail:
      'Clinch connects your number through Gupshup, a Meta Business Solution Provider: the app, the signup link, and incoming-message webhooks are set up for you. You still own your Meta business portfolio and WhatsApp Business Account.',
  },
  {
    title: 'AI shopping agent',
    detail:
      'Agent behaviour, MCP, and WhatsApp routing run on Clinch’s agent service. You do not configure MCP in a client platform.',
  },
]

export function workspaceSetupIntro() {
  return {
    kicker: 'Getting started',
    title: 'Set up My Platform',
    subtitle: 'Follow this guide in order. Expand a step when you need the how-to.',
    lead: '',
  }
}
